// Filter command bar — Attio/Stripe-style. A row of segmented pills floating
// near the top of the map. Click a pill to open a popover with the actual
// options. Soft, rounded, generous padding, no all-caps tiny chips.
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { CSSProperties, MouseEvent as ReactMouseEvent, MouseEventHandler } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import type { FilterState } from '../lib/outbreaks';
import {
  PATHOGEN_COLOR,
  PATHOGEN_LABEL,
  PATHOGEN_ORDER,
} from '../lib/palette';
import type { Pathogen, Syndrome, OutbreakStatus } from '../types/supabase';

interface Props {
  filters: FilterState;
  onChange: (next: FilterState) => void;
  visibleCount: number;
  totalCount: number;
  /** Inline mode renders the filter row without a glass-pill wrapper, for use
   *  inside the sidebar. The popover panels open below in-flow. */
  inline?: boolean;
}

type Segment = 'pathogen' | 'syndrome' | 'status';

const SYNDROMES: Syndrome[] = ['HPS', 'HFRS', 'unspecified'];
const STATUSES: OutbreakStatus[] = ['active', 'concluded', 'historical'];

export function FilterDrawer({ filters, onChange, visibleCount, totalCount, inline }: Props) {
  const [open, setOpen] = useState<Segment | null>(null);
  // Screen-space anchor of the chip that opened the dropdown, so the
  // inline popover can float (fixed) above the rest of the sidebar
  // instead of pushing/overlapping content in normal flow.
  const [anchor, setAnchor] = useState<{ left: number; top: number } | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  // The inline dropdown is portaled to <body> (the sidebar's backdrop-blur
  // creates a containing block that would otherwise trap fixed-position
  // children), so it sits outside rootRef — track it separately for
  // outside-click dismissal.
  const popRef = useRef<HTMLDivElement | null>(null);

  const openSegment = (seg: Segment) =>
    (e: ReactMouseEvent<HTMLButtonElement>) => {
      if (open === seg) { setOpen(null); return; }
      const r = e.currentTarget.getBoundingClientRect();
      setAnchor({ left: r.left, top: r.bottom });
      setOpen(seg);
    };

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t) || popRef.current?.contains(t)) return;
      setOpen(null);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(null); };
    // The inline dropdown is fixed-positioned to a captured anchor. Only a
    // viewport resize can move the trigger here (the chip sits in a
    // non-scrolling sidebar section), so close on resize but NOT on
    // scroll — a global capture-phase scroll listener also fires for
    // inner scroll containers (the menu / events list re-rendering on a
    // checkbox toggle), which was slamming the menu shut on every click.
    const onResize = () => setOpen(null);
    window.addEventListener('mousedown', onDocClick);
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('mousedown', onDocClick);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  const toggle = <T extends string>(key: keyof FilterState, value: T) => {
    const cur = filters[key] as Set<string>;
    const next = new Set(cur);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    onChange({ ...filters, [key]: next });
  };

  const segCounts: Record<Segment, number> = {
    pathogen: filters.pathogens.size,
    syndrome: filters.syndromes.size,
    status: filters.statuses.size,
  };


  const wrapperClass = inline
    ? 'select-none'
    : 'pointer-events-auto select-none';
  const rowClass = inline
    ? 'flex flex-wrap items-center gap-1'
    : 'surface-glass flex h-10 items-center gap-1 px-1.5';
  const DROPDOWN_W = 340;
  const dropdownStyle: CSSProperties | undefined =
    inline && anchor
      ? {
          left: Math.max(
            12,
            Math.min(anchor.left, window.innerWidth - DROPDOWN_W - 12),
          ),
          top: anchor.top + 6,
          width: DROPDOWN_W,
          maxWidth: 'calc(100vw - 24px)',
          maxHeight: `calc(100vh - ${anchor.top + 6}px - 16px)`,
        }
      : undefined;
  const popClass = inline
    ? 'fade-in surface-elevated fixed z-[60] overflow-y-auto rounded-xl p-3 shadow-xl ring-1 ring-[var(--color-line)]'
    : 'fade-in surface-elevated mt-2 w-[420px] p-3';

  const panels = (
    <>
      {open === 'pathogen' && (
        <PathogenPanel
          selected={filters.pathogens}
          onToggle={(p) => toggle('pathogens', p)}
          onClear={() => onChange({ ...filters, pathogens: new Set() })}
        />
      )}
      {open === 'syndrome' && (
        <PillRow
          title="Syndrome"
          items={SYNDROMES}
          selected={filters.syndromes}
          onToggle={(s) => toggle('syndromes', s)}
          onClear={() => onChange({ ...filters, syndromes: new Set() })}
        />
      )}
      {open === 'status' && (
        <PillRow
          title="Status"
          items={STATUSES}
          selected={filters.statuses}
          onToggle={(s) => toggle('statuses', s)}
          onClear={() => onChange({ ...filters, statuses: new Set() })}
          dotForItem={(s) =>
            s === 'active' ? 'var(--color-signal)' : s === 'concluded' ? 'var(--color-text-3)' : 'var(--color-text-4)'
          }
        />
      )}
    </>
  );

  const popover = open
    ? inline
      ? createPortal(
          <div ref={popRef} className={popClass} style={dropdownStyle}>
            {panels}
          </div>,
          document.body,
        )
      : (
          <div className={popClass} style={dropdownStyle}>
            {panels}
          </div>
        )
    : null;

  return (
    <div ref={rootRef} className={wrapperClass}>
      <div className={rowClass}>
        {!inline && (
          <>
            <Counter visible={visibleCount} total={totalCount} />
            <span className="mx-0.5 h-4 w-px bg-[var(--color-line)]" />
          </>
        )}
        <Segment label="Pathogen"   count={segCounts.pathogen}   active={open === 'pathogen'}   onClick={openSegment('pathogen')} />
        <Segment label="Syndrome"   count={segCounts.syndrome}   active={open === 'syndrome'}   onClick={openSegment('syndrome')} />
        <Segment label="Status"     count={segCounts.status}     active={open === 'status'}     onClick={openSegment('status')} />
      </div>

      {popover}
    </div>
  );
}

function Counter({ visible, total }: { visible: number; total: number }) {
  return (
    <div className="flex items-baseline gap-1.5 px-2.5 py-1 text-[13px]">
      <span className="font-semibold tabular-nums text-[var(--color-text)]">{visible}</span>
      <span className="text-[var(--color-text-4)]">/</span>
      <span className="tabular-nums text-[var(--color-text-3)]">{total}</span>
      <span className="ml-0.5 text-[12px] font-medium text-[var(--color-text-3)]">events</span>
    </div>
  );
}

function Segment({
  label, count, active, onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: MouseEventHandler<HTMLButtonElement>;
}) {
  const hasFilter = count > 0;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={
        'inline-flex h-7 items-center gap-1.5 rounded-md border px-2.5 text-[12px] font-medium transition-all ' +
        (active
          ? 'border-[var(--color-accent)] bg-[var(--color-accent-soft)] text-[var(--color-accent)] shadow-[0_1px_2px_rgba(82,71,229,0.12)]'
          : hasFilter
          ? 'border-[var(--color-line-strong)] bg-white text-[var(--color-text)] shadow-sm hover:border-[var(--color-accent)] hover:text-[var(--color-accent)]'
          : 'border-[var(--color-line)] bg-[var(--color-surface-2)] text-[var(--color-text-2)] hover:border-[var(--color-line-strong)] hover:bg-white hover:text-[var(--color-text)]')
      }
    >
      <span>{label}</span>
      {hasFilter && (
        <span
          className={
            'rounded-full px-1.5 py-px text-[10.5px] font-semibold tabular-nums ' +
            (active ? 'bg-[var(--color-accent)] text-white' : 'bg-[var(--color-accent-soft)] text-[var(--color-accent)]')
          }
        >
          {count}
        </span>
      )}
      <ChevronDown
        size={12}
        strokeWidth={2.25}
        className={
          'shrink-0 opacity-60 transition-transform ' +
          (active ? 'rotate-180' : '')
        }
      />
    </button>
  );
}

function PathogenPanel({
  selected, onToggle, onClear,
}: {
  selected: Set<string>;
  onToggle: (p: Pathogen) => void;
  onClear: () => void;
}) {
  return (
    <div>
      <PanelHeader title="Pathogen" hasSelection={selected.size > 0} onClear={onClear} />
      <div className="grid grid-cols-2 gap-1">
        {PATHOGEN_ORDER.map(p => (
          <PathogenRow
            key={p}
            pathogen={p}
            active={selected.has(p)}
            onClick={() => onToggle(p)}
          />
        ))}
      </div>
    </div>
  );
}

function PathogenRow({
  pathogen, active, onClick,
}: { pathogen: Pathogen; active: boolean; onClick: () => void }) {
  const info = PATHOGEN_LABEL[pathogen];
  const color = PATHOGEN_COLOR[pathogen];
  return (
    <button
      type="button"
      onClick={onClick}
      title={info.name}
      role="checkbox"
      aria-checked={active}
      className={
        'group flex h-8 w-full items-center gap-2 rounded-md px-2 text-left text-[13px] transition-colors ' +
        (active ? 'bg-[var(--color-accent-soft)]' : 'hover:bg-[var(--color-surface-2)]')
      }
    >
      {/* Always-visible checkbox so selection state reads at a glance. */}
      <span
        aria-hidden
        className={
          'flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors ' +
          (active
            ? 'border-[var(--color-accent)] bg-[var(--color-accent)] text-white'
            : 'border-[var(--color-line-strong)] bg-white text-transparent')
        }
      >
        <Check size={12} strokeWidth={3} />
      </span>
      <span
        className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
        style={{
          background: color,
          boxShadow: '0 0 0 2px white, 0 0 0 3px rgba(0,0,0,0.06)',
        }}
      />
      <span className="flex flex-1 items-baseline gap-1.5 truncate">
        <span className="mono text-[11px] font-semibold text-[var(--color-text)]">
          {info.abbreviation}
        </span>
        <span className="truncate text-[12px] text-[var(--color-text-3)]">
          {info.name.replace(' virus', '')}
        </span>
      </span>
    </button>
  );
}

function PillRow<T extends string>({
  title, items, selected, onToggle, onClear, dotForItem,
}: {
  title: string;
  items: readonly T[];
  selected: Set<string>;
  onToggle: (v: T) => void;
  onClear: () => void;
  dotForItem?: (v: T) => string;
}) {
  return (
    <div>
      <PanelHeader title={title} hasSelection={selected.size > 0} onClear={onClear} />
      <div className="flex flex-wrap gap-1.5">
        {items.map(v => {
          const active = selected.has(v);
          const dot = dotForItem?.(v);
          return (
            <button
              key={v}
              type="button"
              onClick={() => onToggle(v)}
              className={
                'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12.5px] font-medium transition-colors ' +
                (active
                  ? 'bg-[var(--color-accent)] text-white'
                  : 'bg-[var(--color-surface-2)] text-[var(--color-text-2)] hover:bg-[var(--color-surface-3)] hover:text-[var(--color-text)]')
              }
            >
              {dot && (
                <span
                  className="inline-block h-1.5 w-1.5 rounded-full"
                  style={{ background: active ? 'rgba(255,255,255,0.85)' : dot }}
                />
              )}
              <span className="capitalize">{v}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function PanelHeader({
  title, hasSelection, onClear,
}: { title: string; hasSelection: boolean; onClear: () => void }) {
  return (
    <div className="mb-2 flex items-center justify-between">
      <span className="text-[12px] font-medium uppercase tracking-wide text-[var(--color-text-3)]">
        {title}
      </span>
      {hasSelection && (
        <button
          type="button"
          onClick={onClear}
          className="text-[12px] font-medium text-[var(--color-text-3)] hover:text-[var(--color-accent)]"
        >
          Clear
        </button>
      )}
    </div>
  );
}
